import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import {
  useClearException,
  useSetException,
} from "@/features/availability/model/useAvailability";
import {
  rangesOverlap,
  templateByDay,
  toApiTime,
  toInputTime,
} from "@/features/availability/model/helpers";
import type {
  AvailabilityException,
  ExceptionWindowInput,
  TemplateWindow,
} from "@/features/availability/types";
import { cn } from "@/shared/lib/cn";
import { formatTimeRange12h } from "@/shared/lib/time";
import { Time12hPicker } from "@/shared/ui";

interface DraftWindow {
  start_time: string;
  end_time: string;
}

type Mode = "FOLLOW_TEMPLATE" | "BLOCKED" | "CUSTOM";

interface DateOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string; // YYYY-MM-DD
  exceptions: AvailabilityException[];
  template: TemplateWindow[];
}

function deriveInitial(
  exceptions: AvailabilityException[],
): { mode: Mode; windows: DraftWindow[] } {
  if (exceptions.length === 0) return { mode: "FOLLOW_TEMPLATE", windows: [] };
  if (exceptions.some((e) => e.kind === "BLOCKED")) {
    return { mode: "BLOCKED", windows: [] };
  }
  return {
    mode: "CUSTOM",
    windows: exceptions
      .filter((e) => e.kind === "AVAILABLE" && e.start_time && e.end_time)
      .map((e) => ({
        start_time: toInputTime(e.start_time),
        end_time: toInputTime(e.end_time),
      })),
  };
}

function formatPrettyDate(iso: string): string {
  // Parse without timezone surprises
  const [y, m, d] = iso.split("-").map((p) => parseInt(p, 10));
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function DateOverrideDialog({
  open,
  onOpenChange,
  date,
  exceptions,
  template,
}: DateOverrideDialogProps) {
  const initial = deriveInitial(exceptions);
  const [mode, setMode] = useState<Mode>(initial.mode);
  const [windows, setWindows] = useState<DraftWindow[]>(initial.windows);
  const [error, setError] = useState<string | null>(null);

  const setException = useSetException();
  const clearException = useClearException();

  // Reset state on the open-transition for a (date) — but not on every parent
  // re-render. Holding `exceptions` in the dep array would re-derive whenever
  // the parent passes a fresh `[]` reference, blowing away the user's edits.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      const next = deriveInitial(exceptions);
      setMode(next.mode);
      setWindows(next.windows);
      setError(null);
    }
    prevOpen.current = open;
    // We intentionally exclude `exceptions` and `date` — `open` toggling false→true
    // is the only signal that should reset state. The dialog is unmounted between
    // dates anyway (parent uses {selectedIso && <Dialog .../>}).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const templateByDayCached = templateByDay(template);
  const dayOfWeek = (() => {
    const [y, m, d] = date.split("-").map((p) => parseInt(p, 10));
    const js = new Date(y, m - 1, d).getDay();
    return (js + 6) % 7;
  })();
  const templateForThisDay = templateByDayCached[dayOfWeek];

  const validateCustom = (): string | null => {
    if (windows.length === 0) return "Add at least one window, or pick a different option.";
    for (const w of windows) {
      if (!w.start_time || !w.end_time) return "Pick a start and end time for each window.";
      if (w.end_time <= w.start_time) return "End time must be after start time.";
    }
    for (let i = 0; i < windows.length; i++) {
      for (let j = i + 1; j < windows.length; j++) {
        if (
          rangesOverlap(
            windows[i].start_time,
            windows[i].end_time,
            windows[j].start_time,
            windows[j].end_time,
          )
        ) {
          return "Windows on the same date cannot overlap.";
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    setError(null);
    if (mode === "FOLLOW_TEMPLATE") {
      await clearException.mutateAsync(date);
      onOpenChange(false);
      return;
    }
    if (mode === "BLOCKED") {
      await setException.mutateAsync({
        date,
        payload: { kind: "BLOCKED", windows: [] },
      });
      onOpenChange(false);
      return;
    }
    const err = validateCustom();
    if (err) {
      setError(err);
      return;
    }
    const apiWindows: ExceptionWindowInput[] = windows.map((w) => ({
      start_time: toApiTime(w.start_time),
      end_time: toApiTime(w.end_time),
    }));
    await setException.mutateAsync({
      date,
      payload: { kind: "AVAILABLE", windows: apiWindows },
    });
    onOpenChange(false);
  };

  const addWindow = () =>
    setWindows((prev) => [...prev, { start_time: "09:00", end_time: "17:00" }]);

  const removeWindow = (idx: number) =>
    setWindows((prev) => prev.filter((_, i) => i !== idx));

  const updateWindow = (idx: number, field: "start_time" | "end_time", value: string) =>
    setWindows((prev) =>
      prev.map((w, i) => (i === idx ? { ...w, [field]: value } : w)),
    );

  const seedFromTemplate = () => {
    if (templateForThisDay.length === 0) {
      setWindows([{ start_time: "09:00", end_time: "17:00" }]);
      return;
    }
    setWindows(
      templateForThisDay.map((t) => ({
        start_time: toInputTime(t.start_time),
        end_time: toInputTime(t.end_time),
      })),
    );
  };

  const isPending = setException.isPending || clearException.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay/60 backdrop-blur-sm" />
        {/* Split positioning (Content) and entry animation (inner) across two
            elements. If both lived on Content, `animate-scale-in`'s `transform:
            scale(...)` keyframe would replace `-translate-x/y-1/2` for 200ms,
            making the dialog briefly appear off-center before snapping back. */}
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(92vw,520px)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 focus:outline-none">
          <div className="flex max-h-[90vh] flex-col animate-scale-in overflow-y-auto rounded-xl border border-border bg-surface-raised p-6 shadow-elevated">
          <div className="mb-4 flex items-start justify-between gap-2">
            <div>
              <Dialog.Title className="text-lg font-semibold tracking-display-tight text-copy">
                {formatPrettyDate(date)}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-copy-muted">
                Override availability for this specific date.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md p-1 text-copy-subtle transition-colors hover:bg-surface-sunken hover:text-copy"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <div className="mb-4 space-y-2">
            <ModeOption
              checked={mode === "FOLLOW_TEMPLATE"}
              onChange={() => setMode("FOLLOW_TEMPLATE")}
              label="Follow my weekly schedule"
              hint={
                templateForThisDay.length === 0
                  ? "No template windows for this day — you'll be unavailable."
                  : `Default: ${templateForThisDay
                      .map((t) => formatTimeRange12h(t.start_time, t.end_time))
                      .join(", ")}`
              }
            />
            <ModeOption
              checked={mode === "BLOCKED"}
              onChange={() => setMode("BLOCKED")}
              label="Block this date"
              hint="You'll be unavailable for shifts all day."
            />
            <ModeOption
              checked={mode === "CUSTOM"}
              onChange={() => {
                setMode("CUSTOM");
                if (windows.length === 0) seedFromTemplate();
              }}
              label="Custom hours for this date"
              hint="Replace your weekly template with one or more windows for this day only."
            />
          </div>

          {mode === "CUSTOM" && (
            <div className="mb-4 rounded-md border border-border bg-surface-sunken/60 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider text-copy-muted">
                  Custom windows
                </span>
                <button
                  type="button"
                  onClick={addWindow}
                  className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-copy transition-colors hover:bg-surface-sunken"
                >
                  Add window
                </button>
              </div>
              <div className="space-y-2">
                {windows.length === 0 && (
                  <p className="text-xs text-copy-muted">
                    Add at least one window for this date.
                  </p>
                )}
                {windows.map((w, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2">
                    <Time12hPicker
                      value={w.start_time}
                      onChange={(v) => updateWindow(idx, "start_time", v)}
                      ariaLabel={`Window ${idx + 1} start`}
                    />
                    <span className="text-xs text-copy-muted">–</span>
                    <Time12hPicker
                      value={w.end_time}
                      onChange={(v) => updateWindow(idx, "end_time", v)}
                      ariaLabel={`Window ${idx + 1} end`}
                    />
                    <button
                      type="button"
                      onClick={() => removeWindow(idx)}
                      className="ml-auto rounded-md px-2 py-1 text-xs font-medium text-copy-muted transition-colors hover:bg-destructive-light hover:text-destructive-foreground"
                      aria-label="Remove window"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="mb-3 rounded-md border border-destructive bg-destructive-light px-3 py-2 text-xs text-destructive-foreground">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-copy transition-colors hover:bg-surface-sunken"
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleSave}
              disabled={isPending}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPending ? "Saving…" : "Save"}
            </button>
          </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ModeOption({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  hint: string;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2 rounded-md border p-3 transition-colors",
        checked
          ? "border-primary bg-primary-light"
          : "border-border hover:bg-surface-sunken",
      )}
    >
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
      />
      <span className="flex flex-col">
        <span className="text-sm font-medium text-copy">{label}</span>
        <span className="text-xs text-copy-muted">{hint}</span>
      </span>
    </label>
  );
}
